import axios from 'axios';
import { API_BASE_URL, AUTH_ENDPOINTS, createAuthHeader } from '../config/api.config';
import { getOrCreateSessionId, clearSessionId } from '../utils/session';
import i18n from '../i18n';

// Set Accept-Language header on axios defaults so all requests include it
axios.defaults.headers.common['Accept-Language'] = i18n.language || 'es';
i18n.on('languageChanged', (lng) => {
  axios.defaults.headers.common['Accept-Language'] = lng;
});

// Client picked by an admin in the header. It rides on every API call that doesn't already carry a
// clientId (incl. /auth/userinfo, so currentUser.customer_id becomes the picked client). The backend
// only honours it for admins; everyone else always gets their own client, whatever is sent.
export const SELECTED_CLIENT_KEY = 'selectedClientId';
axios.interceptors.request.use((config) => {
  const selected = localStorage.getItem(SELECTED_CLIENT_KEY);
  if (selected && config.url?.startsWith(API_BASE_URL)
      && !/[?&]clientId=/.test(config.url) && config.params?.clientId == null) {
    config.params = { ...config.params, clientId: selected };
  }
  return config;
});

// Create an event bus for handling auth events across the app
export const authEvents = {
  sessionExpired: new Set(),
  
  // Subscribe to session expiry event
  onSessionExpired(callback) {
    this.sessionExpired.add(callback);
    return () => this.sessionExpired.delete(callback);
  },
  
  // Trigger session expiry event
  emitSessionExpired() {
    this.sessionExpired.forEach(callback => callback());
  }
};

// Set auth token and session ID in axios defaults
const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.defaults.headers.common['X-Session-Id'] = getOrCreateSessionId();
  } else {
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Session-Id'];
  }
};

// Login user
const login = async (username, password) => {
  try {
    const response = await axios.post(
      AUTH_ENDPOINTS.LOGIN,
      {
        username,
        password,
      }
    );

    const { access_token } = response.data;
    
    // Save token to localStorage
    localStorage.setItem('access_token', access_token);
    
    // Set axios default headers
    setAuthToken(access_token);
    
    // Get and store user information
    const userInfo = await getUserInfo();
    return userInfo;
  } catch (error) {
    
    throw error;
  }
};

// Get user information
const getUserInfo = async () => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) throw new Error('No authentication token found');
    
    const response = await axios.get(AUTH_ENDPOINTS.USER_INFO, {
      headers: createAuthHeader(token)
    });
    
    // Store user info in localStorage
    localStorage.setItem('userInfo', JSON.stringify(response.data));
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Clients an admin can switch between. Only call it for admins: the backend answers 403 otherwise.
const getClients = async () => {
  const response = await axios.get(AUTH_ENDPOINTS.CLIENTS);
  return response.data.clients;
};

// Verify token validity
const verifyToken = async () => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    
    // Replace with userinfo check if /verify endpoint doesn't exist
    try {
      await axios.get(AUTH_ENDPOINTS.VERIFY_TOKEN, {
        headers: createAuthHeader(token)
      });
      return true;
    } catch (verifyError) {
      // If verify endpoint fails, try userinfo as a fallback
      await axios.get(AUTH_ENDPOINTS.USER_INFO, {
        headers: createAuthHeader(token)
      });
      return true;
    }
  } catch (error) {
    return false; // Let the calling code handle logout
  }
};

// Logout user
const logout = () => {
  // Clear localStorage
  localStorage.removeItem('access_token');
  localStorage.removeItem('userInfo');
  localStorage.removeItem(SELECTED_CLIENT_KEY);

  // Clear session ID
  clearSessionId();

  // Clear axios headers
  setAuthToken(null);

  return true;
};

// Initialize auth on app load
const initAuth = () => {
  const token = localStorage.getItem('access_token');
  if (token) {
    setAuthToken(token);
    
    // Set up axios interceptor for handling session expiry
    setupAuthInterceptor();
    
    return true;
  }
  return false;
};

// Set up axios response interceptor to catch 401 errors
let interceptorId = null;
const setupAuthInterceptor = () => {
  // Remove existing interceptor if any
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
  }

  // Add response interceptor
  interceptorId = axios.interceptors.response.use(
    response => response,
    error => {
      const statusCode = error?.response?.status;
      
      // If unauthorized or forbidden - session expired or invalid token
      if (statusCode === 401 || statusCode === 403) {
        
        // Only emit once - prevent multiple dialogs
        if (localStorage.getItem('access_token')) {
          // Clean up auth data
          logout();
          
          // Trigger session expired event
          authEvents.emitSessionExpired();
        }
      }
      
      return Promise.reject(error);
    }
  );
};

export const authService = {
  login,
  logout,
  getUserInfo,
  getClients,
  verifyToken,
  initAuth,
  setAuthToken
};